import { GridType, ClassAllocation, DepartmentClassConfig } from '../types/grid';
import { Department } from '../types/data';

/**
 * Calculate the effective minutes for a class based on grid type, department, and class-specific overrides
 */
export function calculateClassMinutes(
  classAllocation: ClassAllocation,
  gridType?: GridType,
  department?: Department
): number {
  // 1. Class-specific override takes highest priority
  if (classAllocation.classMinutes) {
    return classAllocation.classMinutes;
  }

  // 2. Department-specific override for this grid type
  if (department?.gridTypeConfigs) {
    const gridConfig = department.gridTypeConfigs.find(config => config.gridTypeId === gridType.id);
    if (gridConfig) {
      return gridConfig.classMinutes;
    }
  }

  // 3. Department default class minutes
  if (department?.defaultClassMinutes) {
    return department.defaultClassMinutes;
  }

  // 4. Grid type department overrides
  if (classAllocation.department && gridType) {
    const departmentOverride = gridType.departmentOverrides.find(
      override => override.departmentName === classAllocation.department
    );
    if (departmentOverride) {
      return departmentOverride.classMinutes;
    }
  }

  // 5. Fall back to grid type default, or legacy default if no grid type
  return gridType?.defaultClassMinutes || 360; // 360 minutes = 6 hours (legacy default)
}

/**
 * Get all available grid types with their configurations
 */
export function getDefaultGridTypes(): GridType[] {
  return [
    {
      id: 'main',
      name: 'Main Grid',
      description: 'Primary timetabling grid for standard classes',
      defaultClassMinutes: 70, // Standard class duration
      linePrefix: 'A-F,G-L',
      availableLines: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'],
      departmentOverrides: [],
      isSystemDefault: true
    },
    {
      id: 'oratory',
      name: 'Oratory Grid',
      description: 'Specialized grid for Oratory classes',
      defaultClassMinutes: 90, // Longer duration for oratory classes
      linePrefix: 'X,Y,Z',
      availableLines: ['X', 'Y', 'Z'],
      departmentOverrides: [
        {
          departmentId: 'oratory',
          departmentName: 'Oratory',
          classMinutes: 90,
          applicableSubjects: ['Public Speaking', 'Drama', 'Debate']
        }
      ],
      isSystemDefault: true
    },
    {
      id: 'ruah',
      name: 'RUAH Grid',
      description: 'Specialized grid for RUAH classes',
      defaultClassMinutes: 60, // Different duration for RUAH classes
      linePrefix: 'R1,R2,R3',
      availableLines: ['R1', 'R2', 'R3'],
      departmentOverrides: [
        {
          departmentId: 'ruah',
          departmentName: 'RUAH',
          classMinutes: 60,
          applicableSubjects: ['RUAH Studies']
        }
      ],
      isSystemDefault: true
    }
  ];
}

/**
 * Get line definitions for a specific grid type
 */
export function getDefaultLinesForGridType(gridType: GridType) {
  const lines = [];
  
  switch (gridType.id) {
    case 'main':
      // Traditional semester-based lines
      for (const lineId of ['A', 'B', 'C', 'D', 'E', 'F']) {
        lines.push({
          id: lineId,
          name: `Line ${lineId}`,
          semester: 'semester1' as const,
          pairedLineId: lineId === 'A' ? 'G' : lineId === 'B' ? 'H' : 
                       lineId === 'C' ? 'I' : lineId === 'D' ? 'J' : 
                       lineId === 'E' ? 'K' : 'L'
        });
      }
      for (const lineId of ['G', 'H', 'I', 'J', 'K', 'L']) {
        lines.push({
          id: lineId,
          name: `Line ${lineId}`,
          semester: 'semester2' as const,
          pairedLineId: lineId === 'G' ? 'A' : lineId === 'H' ? 'B' : 
                       lineId === 'I' ? 'C' : lineId === 'J' ? 'D' : 
                       lineId === 'K' ? 'E' : 'F'
        });
      }
      break;
      
    case 'oratory':
      // Full-year lines for oratory
      for (const lineId of gridType.availableLines) {
        lines.push({
          id: lineId,
          name: `Oratory ${lineId}`,
          semester: 'fullyear' as const
        });
      }
      break;
      
    case 'ruah':
      // Full-year lines for RUAH
      for (const lineId of gridType.availableLines) {
        lines.push({
          id: lineId,
          name: `RUAH ${lineId}`,
          semester: 'fullyear' as const
        });
      }
      break;
      
    default:
      // Generic full-year lines
      for (const lineId of gridType.availableLines) {
        lines.push({
          id: lineId,
          name: `Line ${lineId}`,
          semester: 'fullyear' as const
        });
      }
  }
  
  return lines;
}

/**
 * Calculate total teaching minutes for a teacher across all grids
 */
export function calculateTeacherTotalMinutes(
  teacherCode: string,
  allGrids: { grid: any, classes: ClassAllocation[] }[],
  departments: Department[]
): number {
  let totalMinutes = 0;
  
  for (const { grid, classes } of allGrids) {
    const teacherClasses = classes.filter(classItem => 
      Object.values(classItem.lineAssignments || {}).includes(teacherCode)
    );
    
    for (const classItem of teacherClasses) {
      const department = departments.find(dept => dept.name === classItem.department);
      const minutes = calculateClassMinutes(classItem, grid.gridType, department);
      
      // Count how many lines this teacher is assigned to for this class
      const assignedLines = Object.values(classItem.lineAssignments || {})
        .filter(assignedTeacher => assignedTeacher === teacherCode).length;
      
      totalMinutes += minutes * assignedLines;
    }
  }
  
  return totalMinutes;
}

/**
 * Check for cross-grid conflicts for a teacher
 */
export function checkCrossGridConflicts(
  teacherCode: string,
  allGrids: { grid: any, classes: ClassAllocation[] }[],
  teacherMaxMinutes: number
): { hasConflicts: boolean, conflicts: string[], totalMinutes: number } {
  const conflicts: string[] = [];
  const totalMinutes = calculateTeacherTotalMinutes(teacherCode, allGrids, []);
  
  if (totalMinutes > teacherMaxMinutes) {
    conflicts.push(`Teacher ${teacherCode} is over-allocated: ${totalMinutes} minutes assigned, ${teacherMaxMinutes} minutes available`);
  }
  
  // Check for schedule conflicts (same time slots across grids)
  const scheduleConflicts = new Set<string>();
  
  for (let i = 0; i < allGrids.length; i++) {
    for (let j = i + 1; j < allGrids.length; j++) {
      const grid1Classes = allGrids[i].classes.filter(c => 
        Object.values(c.lineAssignments || {}).includes(teacherCode)
      );
      const grid2Classes = allGrids[j].classes.filter(c => 
        Object.values(c.lineAssignments || {}).includes(teacherCode)
      );
      
      // This would need more sophisticated scheduling logic
      // For now, we assume if a teacher is in multiple grids, there might be conflicts
      if (grid1Classes.length > 0 && grid2Classes.length > 0) {
        scheduleConflicts.add(`Potential schedule conflict between ${allGrids[i].grid.name} and ${allGrids[j].grid.name}`);
      }
    }
  }
  
  conflicts.push(...Array.from(scheduleConflicts));
  
  return {
    hasConflicts: conflicts.length > 0,
    conflicts,
    totalMinutes
  };
}